import React, { useRef } from "react";
import styled from "styled-components";

import { colors } from "utils";
import { MATH_RUBRIC_URL } from "constants/rubricLinks";
import {
  DropdownContainer,
  SelectDropdown,
} from "components/shared/FilterDropdowns/FilterDropdowns";
import {
  BodyRow,
  Cell,
  ColumnHeader,
  HeaderButton,
  LevelTag,
  SortGlyph,
  SortableHeader,
  Table,
  TableWrapper,
} from "./reportTableStyles";
import { Tooltip } from "components/shared/Tooltip/Tooltip";
import { EM_DASH } from "./studentReportTable";
import { RubricLink } from "./RubricLink";
import { useIsTruncated } from "./useIsTruncated";
import { ProficiencyDistribution } from "./ProficiencyDistribution";
import {
  ALL_DOMAINS,
  ALL_GRADES,
  NEED_MORE_DATA,
  PROFICIENCY_SORT_KEYS,
  WIP_DEFINITION,
  firmLevel,
  gradeLabel,
  lastLessonText,
  nextProficiencySort,
  questionLabel,
} from "./proficiencyStandards";

// Math Proficiency (final frame 4422:123602) — every standard this student has
// answered on, what they can do on it now, and the ten answers that say so.
//
// The page asks for answered standards only, so every row is a firm level or
// WIP. A row with no evidence still renders, as an em dash, rather than being
// treated as a failure.

// The frame's grid (4485:73032): Most Recent 150, Proficiency 160 and the
// question grid 504 are fixed; Standard is the flexible column and takes what
// is left -- 250 at the frame's 1064.
const COLUMN_WIDTHS = [null, 150, 160, 504];

export const MathProficiency = ({
  rows = [],
  distribution = {},
  filters = {},
  grade = null,
  domainCode = null,
  onGradeChange,
  onDomainChange,
  onQuestionClick,
  downloadSlot,
  sort = null,
  onSortChange,
}) => {
  const grades = filters.grades || [];
  const domains = filters.domains || [];
  const selectedDomain =
    domains.find((entry) => entry.code === domainCode) || null;

  const sortHeader = (column, label, labelAlign) => (
    <SortableHeader scope="col">
      <HeaderButton
        type="button"
        $centered
        $labelAlign={labelAlign}
        onClick={() => onSortChange(nextProficiencySort(sort, column))}
      >
        <span>{label}</span>
        <SortGlyph direction={sort?.column === column ? sort.direction : null} />
      </HeaderButton>
    </SortableHeader>
  );

  return (
    <ProficiencyTab>
      {/* Class Insights' paragraph, except the link, which the final frame
          words "View the rubric details" with no trailing period. */}
      <Definition data-testid="proficiency-definition">
        The Math Proficiency levels reflect student accuracy on the 5 most
        recent Solo Time questions in each standard (WIP means fewer than 5
        questions answered). Use the levels to determine who needs reteaching,
        who needs more practice, and who is ready to extend or lead a
        discussion.{" "}
        <RubricLink href={MATH_RUBRIC_URL} variant="inline">
          View the rubric details
        </RubricLink>
      </Definition>

      <FilterRow data-testid="proficiency-filter-row">
        <FilterDropdown>
          <SelectDropdown
            label="Grade"
            value={grade}
            displayText={grade ? gradeLabel(grade) : ALL_GRADES}
            options={[
              { value: null, label: ALL_GRADES },
              ...grades.map((entry) => ({
                value: entry.grade,
                label: gradeLabel(entry.grade),
              })),
            ]}
            onChange={onGradeChange}
          />
        </FilterDropdown>
        <FilterDropdown>
          <SelectDropdown
            label="Domain"
            value={domainCode}
            displayText={selectedDomain?.name || ALL_DOMAINS}
            options={[
              { value: null, label: ALL_DOMAINS },
              ...domains.map((entry) => ({
                value: entry.code,
                label: entry.name,
              })),
            ]}
            onChange={onDomainChange}
          />
        </FilterDropdown>
        {downloadSlot ? <DownloadSlot>{downloadSlot}</DownloadSlot> : null}
      </FilterRow>

      <ProficiencyDistribution distribution={distribution} />

      <ProficiencyTableWrapper>
        <ProficiencyTable>
          <colgroup>
            {COLUMN_WIDTHS.map((width, index) => (
              <col key={index} style={width ? { width } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {sortHeader(PROFICIENCY_SORT_KEYS.STANDARD, "Standard")}
              {sortHeader(PROFICIENCY_SORT_KEYS.LAST_LESSON, "Most Recent")}
              {/* Left-aligned, as the frame draws it beside the other two. */}
              {sortHeader(PROFICIENCY_SORT_KEYS.LEVEL, "Proficiency", "left")}
              <ColumnHeader scope="col">Last 10 Questions</ColumnHeader>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <BodyRow
                key={row.standard_code}
                $clickable={false}
                data-testid="proficiency-row"
              >
                <StandardCell>
                  <StandardClamp code={row.standard_code} name={row.standard_name} />
                </StandardCell>
                <MostRecentCell>{lastLessonText(row.last_lesson_date)}</MostRecentCell>
                <LevelCell>
                  <ProficiencyTag row={row} />
                </LevelCell>
                <QuestionsCell>
                  <QuestionGrid row={row} onQuestionClick={onQuestionClick} />
                </QuestionsCell>
              </BodyRow>
            ))}
          </tbody>
        </ProficiencyTable>
      </ProficiencyTableWrapper>
    </ProficiencyTab>
  );
};

/**
 * The code and the name, three lines at most. Only a name the clamp actually
 * cut shows in full in the shared tooltip, and only then can the text take
 * focus. The bubble is silent to a screen reader: the full name is already in
 * the cell.
 */
const StandardClamp = ({ code, name }) => {
  const clampRef = useRef(null);
  const truncated = useIsTruncated(clampRef, [code, name]);

  return (
    <Tooltip
      tip={truncated ? name : null}
      placement="bottom"
      align="start"
      dismissOnEscape
      hiddenFromScreenReaders
    >
      <StandardText
        ref={clampRef}
        data-testid="standard-text"
        tabIndex={truncated ? 0 : undefined}
      >
        <StandardCode>{code}</StandardCode>
        <br />
        <StandardName>{name}</StandardName>
      </StandardText>
    </Tooltip>
  );
};

/**
 * The Current Proficiency Level cell.
 *
 * Three states, not two: a firm level, "WIP" for a standard with some evidence
 * but not yet five answers, and an em dash for one with none at all.
 * Collapsing the last two would tell a teacher we are working on a standard the
 * student has never seen.
 */
const ProficiencyTag = ({ row }) => {
  const level = firmLevel(row);
  if (level) return <LevelTag level={level} />;
  if (row.status) return <WipTrigger standardCode={row.standard_code} />;
  return <span aria-hidden="true">{EM_DASH}</span>;
};

// The WIP pill explains itself on hover, focus, click or tap. The definition
// reaches assistive tech once, through aria-describedby; the bubble is silent.
// The standard code keeps each row's description id unique.
//
// The frame's tooltip is 205 wide with its 16px arrow, so the bubble is 189:
// the width that breaks "WIP means fewer than / 5 questions answered".
const WIP_BUBBLE_WIDTH = 189;

const WipTrigger = ({ standardCode }) => {
  const definitionId = `wip-definition-${standardCode}`;
  return (
    <>
      <Tooltip
        tip={WIP_DEFINITION}
        placement="right"
        maxWidth={WIP_BUBBLE_WIDTH}
        arrowSize="16x12"
        toggleOnClick
        dismissOnEscape
        hiddenFromScreenReaders
      >
        <WipTag type="button" aria-describedby={definitionId}>
          {NEED_MORE_DATA}
        </WipTag>
      </Tooltip>
      <WipDefinition id={definitionId}>{WIP_DEFINITION}</WipDefinition>
    </>
  );
};

/**
 * The last ten answers, newest first.
 *
 * The label is the attempt's own ordinal, so a row whose student has answered
 * twelve times reads Q12 down to Q3 — the numbers are the child's history, not
 * the grid's ten slots, and a teacher who sees Q12 knows there are two earlier
 * answers this row is not showing.
 */
const QuestionGrid = ({ row, onQuestionClick }) => {
  const attempts = row.attempts || [];
  if (!attempts.length) {
    return <NoQuestions>{EM_DASH}</NoQuestions>;
  }

  return (
    <Questions>
      {attempts.map((attempt) => (
        <Question
          key={`${attempt.question_id}-${attempt.ordinal}`}
          data-testid="question-chip"
        >
          <QuestionBox
            type="button"
            onClick={() => onQuestionClick?.(attempt, row)}
            aria-label={`${questionLabel(attempt)} on ${row.standard_code}: ${
              attempt.correct ? "correct" : "incorrect"
            }`}
          >
            <QuestionLabel>{questionLabel(attempt)}</QuestionLabel>
            <AnswerIcon correct={attempt.correct} />
          </QuestionBox>
        </Question>
      ))}
    </Questions>
  );
};

/** The filled 24px mark under a question: a tick, or a cross. */
const AnswerIcon = ({ correct }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
    data-testid={correct ? "answer-correct" : "answer-incorrect"}
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      fill={correct ? colors.green500 : colors.pink500}
    />
    {correct ? (
      <path
        d="M15.5149 8C15.2747 8 15.0344 8.09499 14.8522 8.28557L10.8283 12.4901L9.14789 10.7342C8.96551 10.5437 8.72539 10.4487 8.48527 10.4487C8.24516 10.4487 8.00485 10.5437 7.82247 10.7342L7.27344 11.3081C6.90885 11.6889 6.90885 12.3121 7.27344 12.6929L8.29145 13.756C8.65585 14.1368 9.25249 14.76 9.61707 15.1408L10.1659 15.7144C10.3483 15.9048 10.5886 16 10.8287 16C11.0688 16 11.3091 15.9048 11.4913 15.7144L16.7267 10.2442C17.0911 9.86344 17.0911 9.2402 16.7267 8.85944L16.1775 8.28557C15.9951 8.09519 15.755 8 15.5149 8Z"
        fill={colors.milkGlass500}
      />
    ) : (
      <path
        d="M15 9L9 15M9 9L15 15"
        stroke={colors.milkGlass500}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

// One block in the page's tab panel, so the panel's flex gap never lands
// between the tab's own sections: their margins are the frame's gaps.
const ProficiencyTab = styled.div``;

const Definition = styled.p`
  margin: 0 0 24px;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
`;

const FilterRow = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-end;
  margin-bottom: 16px;
`;

// 200px each, as the frame draws Grade and Domain (4485:72987), not a share
// of the row -- the shared container would otherwise stretch them to meet the
// button. Label and trigger trimmed to the frame's 17px and 40px.
const FilterDropdown = styled(DropdownContainer)`
  flex: 0 0 200px;
  width: 200px;

  label {
    line-height: 17px;
  }

  button[aria-haspopup] {
    height: 40px;
    box-sizing: border-box;
  }
`;

// Hard right, opposite the two dropdowns, as the frame places it (4485:72995).
const DownloadSlot = styled.div`
  margin-left: auto;
`;

// 16px below the distribution's bar block, as the final frame spaces them.
const ProficiencyTableWrapper = styled(TableWrapper)`
  margin-top: 16px;
`;

const ProficiencyTable = styled(Table)`
  table-layout: fixed;
`;

const StandardCell = styled(Cell)`
  padding: 16px;
  background: ${colors.gray50};
`;

// Three lines for the whole cell -- the code and two of the name -- then an
// ellipsis. The frame's own four-line row is what its annotation rules out.
const StandardText = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: ${colors.nightlyWoods500};
`;

// Bold in the table's green, not the link blue: the frame draws the code as
// part of the text (4485:73039), not as something to click.
const StandardCode = styled.span`
  font-weight: 700;
`;

const StandardName = styled.span`
  font-weight: 400;
`;

const MostRecentCell = styled(Cell)`
  text-align: center;
`;

const LevelCell = styled(Cell)`
  text-align: center;
  background: ${colors.gray50};
`;

const QuestionsCell = styled(Cell)`
  background: ${colors.gray50};
`;

const Questions = styled.div`
  display: flex;
  align-items: flex-start;
  /* The row holds ten chips at their natural width; a narrow viewport scrolls
     the table rather than stacking them, because a wrapped grid stops reading
     as one sequence of answers. */
  flex-wrap: nowrap;
`;

const Question = styled.div`
  display: flex;
`;

// The chip's whole box is its button: the label and its mark, 12px either
// side. It fills Blue/50 on hover and on keyboard focus, with square corners;
// the label keeps its colour.
const QuestionBox = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  padding: 0 12px;
  border: none;
  background: none;
  cursor: pointer;

  &:hover {
    background: ${colors.blue50};
  }

  &:focus-visible {
    background: ${colors.blue50};
    outline: 2px solid ${colors.blue500};
    outline-offset: 2px;
  }
`;

const QuestionLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  color: ${colors.blue700};
  text-decoration: underline;
  white-space: nowrap;
`;

const NoQuestions = styled.span`
  color: ${colors.milkGlass950};
`;

// The work-in-progress tag: neutral on purpose. Tinting it by how the
// partial evidence is trending would put a colour a teacher reads as a level on
// a cell that explicitly has none. 3px of padding inside the 1px border makes
// it the frame's 30px, as tall as a Level pill.
const WipTag = styled.button`
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 11px;
  background: #f3f4f6;
  border: 1px solid #acafad;
  color: #1f2937;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
  white-space: nowrap;
`;

const WipDefinition = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
